import { toast } from 'react-toastify';
import { getReportProfile } from './reportProfiles';

/**
 * Generate standardized print HTML with header and footer
 * @param {Object} options - Configuration object
 * @param {string} options.contentHTML - Main content HTML to print
 * @param {string} options.title - Document title
 * @param {string} options.documentName - Document name (e.g., "Product Details")
 * @param {string} options.clientName - Client/Organization name
 * @param {string} options.clientAddress - Client address
 * @param {string} options.clientLogoUrl - URL to client logo
 * @param {string} options.primeCleanLogoUrl - URL to PrimeClean logo
 * @param {string} options.revisionNo - Document revision number
 * @param {string} options.formatNo - QA format/document-control number (e.g. "QA-SOP-011-F-01-00"), shown in footer
 * @param {string} options.unitName - Production unit/line name
 * @param {string} options.timestamp - Timestamp of generation
 * @returns {string} Complete print-ready HTML
 */
export const generatePrintHTML = (rawOptions = {}) => {
  // options.reportKey → apply that report's header/footer profile
  // (Settings → App Configuration → Reports & Documents).
  const profile = rawOptions.reportKey ? getReportProfile(rawOptions.reportKey) : null;
  const options = profile ? {
    ...rawOptions,
    documentName: profile.document_name || rawOptions.documentName,
    formatNo: profile.format_no || rawOptions.formatNo,
    revisionNo: profile.revision_no || rawOptions.revisionNo,
    clientLogoUrl: profile.show_logos ? rawOptions.clientLogoUrl : '',
    primeCleanLogoUrl: profile.show_logos ? rawOptions.primeCleanLogoUrl : '',
    footerText: profile.footer_text,
    effectiveDate: profile.effective_date,
  } : rawOptions;
  const { footerText = '', effectiveDate = '' } = options;
  const {
    contentHTML = '',
    systemName = 'PrimeClean',
    title = `${systemName} Document`,
    documentName = 'Report',
    clientName = 'Client Name',
    clientAddress = 'Client Address',
    clientLogoUrl = '',
    primeCleanLogoUrl = '',
    revisionNo = '01',
    formatNo = '',
    unitName = 'All Units',
    fitOnePage = false,
    timestamp = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } = options;

  const hostName = window.location.hostname;

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          html, body {
            height: 100%;
          }

          body {
            font-family: 'Segoe UI', 'Trebuchet MS', Arial, sans-serif;
            padding: 22px;
            color: #0f172a;
            line-height: 1.45;
            background: white;
            font-size: 12.5px;
            display: flex;
            flex-direction: column;
            min-height: 100vh;
          }

          .print-header {
            border-bottom: 2px solid #00d4aa;
            padding-bottom: 8px;
            margin-bottom: 12px;
            display: grid;
            grid-template-columns: 1fr 2fr 1fr;
            gap: 16px;
            align-items: center;
          }

          .print-header .logo-section {
            text-align: center;
          }

          .print-header .logo-section img {
            max-width: 64px;
            max-height: 40px;
            margin-bottom: 0;
          }

          .print-header .logo-section .logo-placeholder {
            width: 64px;
            height: 40px;
            background: #f1f5f9;
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #64748b;
            font-size: 9px;
            margin: 0 auto;
          }

          .print-header .client-info {
            text-align: center;
          }

          .print-header .client-info h1 {
            font-size: 15px;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 3px;
          }

          .print-header .client-info p {
            font-size: 11px;
            color: #64748b;
            margin: 1px 0;
          }

          .print-header .primeclean-logo {
            text-align: center;
          }

          .print-header .primeclean-logo img {
            max-width: 64px;
            max-height: 40px;
            margin-bottom: 0;
          }

          .print-header .primeclean-logo .logo-placeholder {
            width: 64px;
            height: 40px;
            background: #f1f5f9;
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #64748b;
            font-size: 9px;
            margin: 0 auto;
          }

          .document-meta {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 12px;
            margin-bottom: 16px;
            padding: 10px 12px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            font-size: 11px;
          }

          .document-meta-item {
            display: flex;
            flex-direction: column;
          }

          .document-meta-item label {
            font-weight: 700;
            color: #475569;
            margin-bottom: 3px;
          }

          .document-meta-item value {
            color: #1e293b;
            word-break: break-word;
          }

          .content-section {
            margin-bottom: 18px;
          }

          h2 {
            font-size: 15px;
            font-weight: 700;
            color: #0f172a;
            margin: 16px 0 8px;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 5px;
          }

          h3 {
            font-size: 13px;
            font-weight: 600;
            color: #1e293b;
            margin: 10px 0 6px;
          }

          h4 {
            font-size: 12px;
            font-weight: 600;
            color: #334155;
            margin: 8px 0 5px;
          }

          p {
            font-size: 11.5px;
            color: #334155;
            margin: 4px 0;
          }

          hr {
            border: none;
            border-top: 1px solid #e2e8f0;
            margin: 10px 0;
          }

          .badge {
            display: inline-block;
            padding: 3px 8px;
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            font-size: 10.5px;
            font-weight: 600;
            background: #f1f5f9;
            color: #0f172a;
            margin-right: 5px;
            margin-bottom: 3px;
          }

          .badge-success {
            background: #dcfce7;
            color: #166534;
            border-color: #bbf7d0;
          }

          .badge-info {
            background: #dbeafe;
            color: var(--primary-dark, #1e40af);
            border-color: #bfdbfe;
          }

          .badge-warning {
            background: #fef3c7;
            color: #b45309;
            border-color: #fde68a;
          }

          .badge-danger {
            background: #fee2e2;
            color: #991b1b;
            border-color: #fecaca;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
            font-size: 11px;
          }

          table th {
            background: #f1f5f9;
            color: #0f172a;
            font-weight: 700;
            padding: 7px 9px;
            text-align: left;
            border: 1px solid #e2e8f0;
          }

          table td {
            padding: 6px 9px;
            border: 1px solid #e2e8f0;
            color: #334155;
          }

          table tr:nth-child(even) {
            background: #f8fafc;
          }

          .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 10px;
          }

          .grid-3 {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 12px;
            margin-bottom: 10px;
          }

          .info-box {
            background: #f8fafc;
            padding: 10px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            margin: 7px 0;
            font-size: 11.5px;
          }

          .warning-box {
            background: #fff7ed;
            padding: 10px;
            border: 1px solid #ffedd5;
            border-radius: 4px;
            margin: 7px 0;
            color: #c2410c;
            font-size: 11.5px;
          }

          .print-footer {
            margin-top: auto;
            padding-top: 12px;
            border-top: 1px solid #e2e8f0;
            text-align: center;
            font-size: 10px;
            color: #64748b;
            flex-shrink: 0;
          }

          .print-footer .footer-line {
            margin: 3px 0;
          }

          .print-footer .footer-system-line {
            font-size: 9px;
            color: #94a3b8;
          }

          .print-footer .footer-format-no {
            font-size: 10px;
            font-weight: 700;
            color: #334155;
          }

          .print-footer .compliance-badge {
            display: inline-block;
            margin-top: 6px;
            padding: 3px 7px;
            background: #f1f5f9;
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 600;
            color: #0f172a;
          }

          @media print {
            body {
              padding: ${fitOnePage ? '6px' : '14px'};
              ${fitOnePage ? 'zoom: 0.72;' : ''}
            }

            .print-footer {
              page-break-inside: avoid;
            }

            .page-break {
              page-break-before: always;
            }

            ${fitOnePage ? '' : 'table, .content-section { page-break-inside: avoid; }'}
          }

          @page {
            ${fitOnePage ? 'size: A4 landscape; margin: 6mm;' : 'margin: 12mm;'}
          }
        </style>
      </head>
      <body>
        <!-- HEADER -->
        <div class="print-header">
          <div class="logo-section">
            ${clientLogoUrl ? `<img src="${clientLogoUrl}" alt="Client Logo" />` : '<div class="logo-placeholder">Client Logo</div>'}
          </div>
          
          <div class="client-info">
            <h1>${clientName}</h1>
            <p>${clientAddress}</p>
          </div>
          
          <div class="primeclean-logo">
            ${primeCleanLogoUrl ? `<img src="${primeCleanLogoUrl}" alt="${systemName} Logo" />` : `<div class="logo-placeholder">${systemName} Logo</div>`}
          </div>
        </div>

        <!-- DOCUMENT METADATA -->
        <div class="document-meta">
          <div class="document-meta-item">
            <label>Document Name:</label>
            <value>${documentName}</value>
          </div>
          <div class="document-meta-item">
            <label>Unit / Section:</label>
            <value>${unitName}</value>
          </div>
          <div class="document-meta-item">
            <label>Rev. No:</label>
            <value>${revisionNo}</value>
          </div>
          <div class="document-meta-item">
            <label>Generated:</label>
            <value>${timestamp}</value>
          </div>
          <div class="document-meta-item" style="grid-column: span 2;">
            <label>Compliance:</label>
            <value>21 CFR Part 11 Compliant</value>
          </div>
        </div>

        <!-- CONTENT -->
        <div class="content-section">
          ${contentHTML}
        </div>

        <!-- FOOTER -->
        <div class="print-footer">
          ${formatNo ? `<div class="footer-line footer-format-no">Format No: ${formatNo} &nbsp;|&nbsp; Rev: ${revisionNo}${effectiveDate ? ` &nbsp;|&nbsp; Effective: ${effectiveDate}` : ''} &nbsp;|&nbsp; Unit: ${unitName}</div>` : ''}
          ${footerText ? `<div class="footer-line">${String(footerText).replace(/</g, '&lt;')}</div>` : ''}
          <div class="footer-line">© ${systemName} Application — Cleaning Validation Management System</div>
          <div class="footer-line">This is a system-generated document. Confidential — For authorized use only.</div>
          <div class="footer-line footer-system-line">System: ${systemName} &nbsp;|&nbsp; Host: ${hostName}</div>
          <div class="compliance-badge">21 CFR Part 11 Compliant Electronic Record</div>
        </div>
      </body>
    </html>
  `;
};

/**
 * Open a print preview window with standardized format
 * @param {Object} options - Same as generatePrintHTML options
 */
export const openPrintPreview = (options = {}) => {
  const { contentHTML = '', systemName = 'PrimeClean', title = `${systemName} Document` } = options;
  
  if (!contentHTML) {
    toast.error('No content available to print');
    return;
  }

  const printHTML = generatePrintHTML(options);
  const printWindow = window.open('', '_blank', 'width=1200,height=900');
  
  if (!printWindow) {
    toast.error('Print window failed to open. Please check popup blocker.');
    return;
  }

  printWindow.document.write(printHTML);
  printWindow.document.close();
  
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 250);
};

/**
 * Get the active unit/line name from localStorage
 */
export const getActivePrintUnitName = () => {
  try {
    const activeLine = localStorage.getItem('activeLine');
    const activePlant = localStorage.getItem('activePlant');
    
    let unitName = 'All Units';
    if (activePlant && activePlant !== 'All') {
      unitName = activePlant;
    }
    if (activeLine && activeLine !== 'All') {
      unitName = activeLine;
    }
    
    return unitName;
  } catch {
    return 'All Units';
  }
};

/**
 * Convert image URL to data URL for embedding in print document
 */
export const toDataUrlIfPossible = async (url) => {
  if (!url || typeof url !== 'string') return null;
  
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};
